import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NativeAdCard from '../components/NativeAdCard';
import AdBanner from '../components/AdBanner';
import QuizCTABanner from '../components/QuizCTABanner';
import Breadcrumbs from '../components/Breadcrumbs';
import { useRecentlyVisited } from '../hooks/useRecentlyVisited';
import { useAds } from '../hooks/useAds';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plane, ArrowLeft, Heart, MessageSquare, Share2, Plus, Image,
  Star, Lightbulb, Camera, ThumbsUp, Flame, ChevronDown, ChevronUp,
  MapPin, Clock, X, Send, ArrowRight, Globe, Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead, { RouteSEO } from '../components/SEOHead';
import ImageUpload from '../components/ImageUpload';
import CityGuide from '../components/CityGuide';
import ContentGate from '../components/ContentGate';
import AuthModal from '../components/AuthModal';

import { API } from '../config';

const POST_TYPES = [
  { value: 'experience', label: 'Experience', icon: Star, cls: 'chip-lounge', color: '#A855F7' },
  { value: 'seat_tip', label: 'Seat Tip', icon: Lightbulb, cls: 'chip-food', color: '#10B981' },
  { value: 'photo', label: 'Photo', icon: Camera, cls: 'chip-gate', color: '#FF4500' },
  { value: 'recommendation', label: 'Tip', icon: ThumbsUp, cls: 'chip-crowd', color: '#1E3A8A' },
];

const TYPE_STYLES = {
  experience: 'chip-lounge',
  seat_tip: 'chip-food',
  photo: 'chip-gate',
  recommendation: 'chip-crowd',
};
const TYPE_LABELS = {
  experience: 'Experience',
  seat_tip: 'Seat Tip',
  photo: 'Photo',
  recommendation: 'Tip',
};

const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Helpful' },
  { key: 'heart', emoji: '❤️', label: 'Love' },
  { key: 'fire', emoji: '🔥', label: 'Amazing' },
  { key: 'helpful', emoji: '⭐', label: 'Recommend' },
];

function RoutePostCard({ post, onReact, onComment, currentUser }) {
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const typeStyle = TYPE_STYLES[post.post_type] || 'chip-crowd';
  const typeLabel = TYPE_LABELS[post.post_type] || post.post_type;

  const totalReactions = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  const handleComment = async () => {
    if (!commentText.trim() || !currentUser) return;
    setSubmitting(true);
    await onComment(post.post_id, commentText);
    setCommentText('');
    setSubmitting(false);
  };

  return (
    <Card className="fly-card overflow-hidden" data-testid="route-post-card">
      <div className="p-4">
        {/* Author */}
        <div className="flex items-start gap-3 mb-3">
          <Avatar className="w-9 h-9 flex-shrink-0">
            <AvatarImage src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_id}`} />
            <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">{post.author_name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">{post.author_name}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${typeStyle}`}>{typeLabel}</span>
              <span className="text-xs text-muted-foreground ml-auto">{timeAgo(post.created_at)}</span>
            </div>
            {post.seat_info && (
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Seat {post.seat_info}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="mb-3">
          <p className={`text-sm leading-relaxed ${!expanded && post.content.length > 200 ? 'line-clamp-3' : ''}`}>
            {post.content}
          </p>
          {post.content.length > 200 && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-[#FF4500] mt-1 flex items-center gap-1 hover:underline">
              {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Read more</>}
            </button>
          )}
        </div>

        {/* Image */}
        {post.image_url && (
          <div className="mb-3 rounded-xl overflow-hidden">
            <img src={post.image_url} alt="Post" className="w-full max-h-72 object-cover" onError={e => e.target.style.display = 'none'} />
          </div>
        )}

        {/* Reactions row */}
        <div className="flex items-center gap-1 flex-wrap">
          {REACTIONS.map(({ key, emoji, label }) => {
            const count = post.reactions?.[key] || 0;
            const userReacted = post.reaction_users?.[currentUser?.user_id] === key;
            return (
              <button
                key={key}
                onClick={() => currentUser ? onReact(post.post_id, key) : toast.error('Log in to react')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all ${
                  userReacted
                    ? 'bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30'
                    : 'bg-muted hover:bg-[#FF4500]/10 hover:text-[#FF4500]'
                }`}
                title={label}
              >
                <span>{emoji}</span>
                {count > 0 && <span>{count}</span>}
              </button>
            );
          })}
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-muted hover:bg-muted/80 ml-auto"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {post.comment_count > 0 ? post.comment_count : ''} Comment{post.comment_count !== 1 ? 's' : ''}
          </button>
        </div>
      </div>

      {/* Comments section */}
      <AnimatePresence>
        {showComments && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <Separator />
            <div className="p-4 space-y-3">
              {(post.comments || []).map((c) => (
                <div key={c.comment_id} className="flex gap-2">
                  <Avatar className="w-6 h-6 flex-shrink-0">
                    <AvatarImage src={c.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.author_id}`} />
                    <AvatarFallback className="text-[8px] bg-muted">{c.author_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <span className="text-xs font-medium">{c.author_name} </span>
                    <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                    <p className="text-xs mt-0.5">{c.content}</p>
                  </div>
                </div>
              ))}
              {currentUser && (
                <div className="flex gap-2 mt-2">
                  <Avatar className="w-6 h-6 flex-shrink-0">
                    <AvatarImage src={currentUser.avatar} />
                    <AvatarFallback className="text-[8px] bg-[#FF4500] text-white">{currentUser.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex gap-1">
                    <Input
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleComment()}
                      className="h-7 text-xs flex-1"
                    />
                    <Button size="sm" className="h-7 w-7 p-0 fly-btn-primary" onClick={handleComment} disabled={submitting || !commentText.trim()}>
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default function RouteCommunity() {
  const { routeId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { add: addVisit } = useRecentlyVisited();
  const [route, setRoute] = useState(null);
  const [flightInfo, setFlightInfo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postOpen, setPostOpen] = useState(false);
  const [postType, setPostType] = useState('experience');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [seatInfo, setSeatInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sidebar data
  const ads = useAds('routes');
  const [relatedRoutes, setRelatedRoutes] = useState([]);
  const [originAirport, setOriginAirport] = useState(null);
  const [destAirport, setDestAirport] = useState(null);

  // Determine if routeId is a flight number (e.g. EK101) or route (RUH-DXB)
  const isFlightNumber = routeId && !routeId.includes('-');
  const [resolvedRouteId, setResolvedRouteId] = useState(isFlightNumber ? null : routeId);
  const [origin, setOrigin] = useState(isFlightNumber ? null : routeId?.split('-')[0]);
  const [destination, setDestination] = useState(isFlightNumber ? null : routeId?.split('-').slice(1).join('-'));

  const loadData = useCallback(async () => {
    if (!routeId) return;
    setLoading(true);
    try {
      // If it's a flight number, look up the flight first
      let actualRouteId = resolvedRouteId;
      if (isFlightNumber) {
        try {
          const flightRes = await axios.get(`${API}/flights/${routeId}`).catch(() =>
            axios.get(`${API}/flights?limit=200`).then(r => ({
              data: r.data.find ? r.data.find(f => f.flight_number === routeId.toUpperCase()) : null
            }))
          );
          const flight = flightRes?.data;
          if (flight && flight.origin_code && flight.destination_code) {
            actualRouteId = `${flight.origin_code}-${flight.destination_code}`;
            setFlightInfo(flight);
            setOrigin(flight.origin_code);
            setDestination(flight.destination_code);
            setResolvedRouteId(actualRouteId);
          } else {
            actualRouteId = routeId;
            setResolvedRouteId(routeId);
          }
        } catch {
          actualRouteId = routeId;
          setResolvedRouteId(routeId);
        }
      }

      const res = await axios.get(`${API}/routes/${actualRouteId}`);
      setRoute(res.data.route);
      setPosts(res.data.posts || []);
      // Track visit
      const o = res.data.route?.origin_code || origin;
      const d = res.data.route?.destination_code || destination;
      if (o && d) addVisit({ type: 'route', href: `/route/${actualRouteId}`, label: `${o} → ${d}`, sublabel: actualRouteId });
    } catch {
      // Route doesn't exist yet
      const o = origin || routeId;
      const d = destination || '';
      setRoute({ route_id: resolvedRouteId || routeId, origin_code: o, destination_code: d, checkin_count: 0 });
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [routeId]);  // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  // Fetch sidebar: related routes + airport info
  useEffect(() => {
    const orig = origin || (routeId && !routeId.includes('-') ? null : routeId?.split('-')[0]);
    const dest = destination || (routeId?.split('-').slice(1).join('-'));
    if (!orig) return;

    // Related routes from same origin (exclude current)
    axios.get(`${API}/routes/list`, { params: { origin: orig, limit: 7 } })
      .then(r => {
        const currentId = resolvedRouteId || routeId;
        setRelatedRoutes((r.data.items || []).filter(rt => rt.route_id !== currentId).slice(0, 5));
      })
      .catch(() => {});

    // Airport info
    axios.get(`${API}/airports/${orig}`).then(r => setOriginAirport(r.data)).catch(() => {});
    if (dest && dest !== 'UNK') {
      axios.get(`${API}/airports/${dest}`).then(r => setDestAirport(r.data)).catch(() => {});
    }
  }, [origin, destination, routeId, resolvedRouteId]);

  const handlePost = async () => {
    if (!content.trim()) { toast.error('Write something first'); return; }
    if (!user) { toast.error('Please log in to post'); return; }
    const rId = resolvedRouteId || routeId;
    const orig = origin || rId?.split('-')[0] || 'UNK';
    const dest = destination || rId?.split('-').slice(1).join('-') || 'UNK';
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/routes/${rId}/posts`,
        {
          route_id: rId,
          origin_code: orig,
          destination_code: dest,
          content,
          post_type: postType,
          image_url: imageUrl || undefined,
          seat_info: seatInfo || undefined,
          is_anonymous: false,
        },
        { withCredentials: true }
      );
      setPosts(prev => [res.data, ...prev]);
      setContent('');
      setImageUrl('');
      setSeatInfo('');
      setPostOpen(false);
      toast.success('Experience shared!');
    } catch {
      toast.error('Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReact = async (postId, reaction) => {
    if (!user) { toast.error('Please log in'); return; }
    const rId = resolvedRouteId || routeId;
    try {
      const res = await axios.post(
        `${API}/routes/${rId}/posts/${postId}/react`,
        { post_id: postId, reaction },
        { withCredentials: true }
      );
      setPosts(prev => prev.map(p => p.post_id === postId ? res.data : p));
    } catch {}
  };

  const handleComment = async (postId, text) => {
    if (!user) { toast.error('Please log in'); return; }
    const rId = resolvedRouteId || routeId;
    try {
      const res = await axios.post(
        `${API}/routes/${rId}/posts/${postId}/comments`,
        { content: text },
        { withCredentials: true }
      );
      setPosts(prev => prev.map(p => p.post_id === postId ? res.data : p));
    } catch {
      toast.error('Failed to comment');
    }
  };

  const displayOrigin = origin || (resolvedRouteId?.split('-')[0]);
  const displayDest = destination || (resolvedRouteId?.split('-').slice(1).join('-'));

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${displayOrigin} → ${displayDest} Route Community`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied!');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <RouteSEO
        origin={displayOrigin}
        destination={displayDest}
        routeId={routeId}
        postCount={posts.length}
      />
      <Navbar />

      {/* Route Hero */}
      <div className="bg-gradient-to-br from-[#111827] to-[#1E3A8A]/80 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <Breadcrumbs
            className="text-white/60 mb-4 [&_a]:text-white/60 [&_a:hover]:text-white [&_svg]:text-white/40"
            items={[
              { label: 'Routes', href: '/routes' },
              { label: `${displayOrigin} → ${displayDest}` },
            ]}
          />
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              {/* Flight number badge if available */}
              {flightInfo && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#FF4500]/20 text-[#FF4500] font-medium border border-[#FF4500]/30">
                    {flightInfo.flight_number}
                  </span>
                  {flightInfo.airline && <span className="text-xs text-white/50">{flightInfo.airline}</span>}
                </div>
              )}
              <div className="flex items-center gap-4 mb-2">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{displayOrigin}</div>
                  {originAirport ? (
                    <div className="mt-1">
                      <div className="text-xs text-white/80 font-medium">{originAirport.city}</div>
                      <div className="text-[10px] text-white/50">{originAirport.name}</div>
                      <div className="text-[10px] text-white/40">{originAirport.country}</div>
                    </div>
                  ) : flightInfo?.origin ? (
                    <div className="text-xs text-white/50 mt-0.5">{flightInfo.origin}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-px w-8 sm:w-16 bg-white/30" />
                  <div className="w-10 h-10 rounded-xl bg-[#FF4500]/30 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-[#FF4500]" />
                  </div>
                  <div className="h-px w-8 sm:w-16 bg-white/30" />
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{displayDest}</div>
                  {destAirport ? (
                    <div className="mt-1">
                      <div className="text-xs text-white/80 font-medium">{destAirport.city}</div>
                      <div className="text-[10px] text-white/50">{destAirport.name}</div>
                      <div className="text-[10px] text-white/40">{destAirport.country}</div>
                    </div>
                  ) : flightInfo?.destination ? (
                    <div className="text-xs text-white/50 mt-0.5">{flightInfo.destination}</div>
                  ) : null}
                </div>
              </div>
              <p className="text-white/60 text-sm">
                {loading ? '...' : `${posts.length} experiences shared`}
                {route?.checkin_count > 0 && ` • ${route.checkin_count} travelers logged this route`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4 mr-1.5" /> Share
              </Button>
              {user && (
                <Dialog open={postOpen} onOpenChange={setPostOpen}>
                  <DialogTrigger asChild>
                    <Button className="fly-btn-primary" data-testid="share-experience-btn">
                      <Plus className="w-4 h-4 mr-1.5" /> Share Experience
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Share Your Experience</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      {/* Post type selector */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">Post Type</label>
                        <div className="grid grid-cols-2 gap-2">
                          {POST_TYPES.map(({ value, label, icon: Icon, color }) => (
                            <button
                              key={value}
                              onClick={() => setPostType(value)}
                              className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm transition-all ${
                                postType === value
                                  ? 'border-[#FF4500] bg-[#FF4500]/8 text-[#FF4500]'
                                  : 'border-border bg-muted/50 hover:border-[#FF4500]/30'
                              }`}
                            >
                              <Icon className="w-4 h-4" style={{ color: postType === value ? '#FF4500' : color }} />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Content */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                          {postType === 'seat_tip' ? 'Your Seat Recommendation' :
                           postType === 'photo' ? 'Describe Your Photo' :
                           postType === 'recommendation' ? 'Your Tip' : 'Your Experience'}
                        </label>
                        <Textarea
                          placeholder={
                            postType === 'seat_tip' ? 'e.g. Seat 14A has extra legroom and great window views...' :
                            postType === 'photo' ? 'Describe what you captured...' :
                            postType === 'recommendation' ? 'Share your best tip for this route...' :
                            'Tell other travelers about your experience on this route...'
                          }
                          value={content}
                          onChange={e => setContent(e.target.value)}
                          rows={4}
                          data-testid="post-content-input"
                        />
                      </div>

                      {/* Seat info (for seat tips) */}
                      {postType === 'seat_tip' && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">Seat Number</label>
                          <Input placeholder="e.g. 14A, 22B" value={seatInfo} onChange={e => setSeatInfo(e.target.value)} />
                        </div>
                      )}

                      {/* Image upload — shown for photo type prominently, optional for others */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block flex items-center gap-1">
                          <Camera className="w-3.5 h-3.5" /> {postType === 'photo' ? 'Upload Photo *' : 'Attach Photo (optional)'}
                        </label>
                        <ImageUpload
                          currentImage={imageUrl || null}
                          onUpload={url => setImageUrl(url || '')}
                          className={postType === 'photo' ? 'h-48' : 'h-28'}
                          placeholder={postType === 'photo' ? 'Click to upload your photo' : 'Add a photo (optional)'}
                          shape="square"
                        />
                      </div>

                      <Button
                        onClick={handlePost}
                        disabled={submitting || !content.trim()}
                        className="fly-btn-primary w-full"
                        data-testid="submit-post-btn"
                      >
                        {submitting ? 'Sharing...' : 'Share Experience'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex gap-6 items-start">

            {/* ── Main content ────────────────────────────── */}
            <div className="flex-1 min-w-0">

          {/* Mobile inline ad */}
          {ads.length > 0 && (
            <div className="lg:hidden mb-5">
              <NativeAdCard ad={ads[0]} variant="route" />
            </div>
          )}

          {/* Filter tabs */}
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-hide">
            <button className="text-xs px-3 py-1.5 rounded-full bg-[#FF4500] text-white font-medium whitespace-nowrap">All Posts</button>
            {POST_TYPES.map(({ value, label, cls }) => (
              <button key={value} className={`text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 whitespace-nowrap transition-colors`}>{label}s</button>
            ))}
          </div>

          {!user && (
            <Card className="fly-card p-4 mb-5 bg-[#FF4500]/5 border-[#FF4500]/20">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm font-medium">Share your experience on this route</p>
                  <p className="text-xs text-muted-foreground">Log in to post experiences, tips, and photos.</p>
                </div>
                <Link to="/">
                  <Button size="sm" className="fly-btn-primary">Join Free</Button>
                </Link>
              </div>
            </Card>
          )}

          {/* Posts */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
            </div>
          ) : posts.length === 0 ? (
            <Card className="fly-card p-10 text-center">
              <Plane className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Be the first to share!</h3>
              <p className="text-sm text-muted-foreground mb-4">
                No experiences shared for {displayOrigin} → {displayDest} yet. Share yours and help fellow travelers.
              </p>
              {user ? (
                <Button className="fly-btn-primary" onClick={() => setPostOpen(true)}>
                  <Plus className="w-4 h-4 mr-1.5" /> Share First Experience
                </Button>
              ) : (
                <Button className="fly-btn-primary" onClick={() => setShowAuthModal(true)}>Join to Share</Button>
              )}
            </Card>
          ) : (
            <ContentGate
              user={user}
              visibleCount={3}
              title={`Join Fly N Out to read all ${posts.length} experiences`}
              message={`Log in free to read all traveler reviews, seat tips, and photos for the ${displayOrigin || ''} → ${displayDest || ''} route.`}
              onSignUp={() => setShowAuthModal(true)}
            >
              {posts.map((post, i) => (
                <motion.div key={post.post_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <RoutePostCard post={post} onReact={handleReact} onComment={handleComment} currentUser={user} />
                </motion.div>
              ))}
            </ContentGate>
          )}

          {/* City Destination Guide */}
          {displayDest && displayDest !== 'UNK' && (
            <CityGuide destinationCode={displayDest} />
          )}

            </div>{/* end main content */}

            {/* ── Sidebar (desktop only) ──────────────────── */}
            <aside className="hidden lg:block flex-shrink-0 w-[300px]">
              <div className="sticky top-20 space-y-5">

                {/* 1. Ad */}
                {ads.length > 0
                  ? <NativeAdCard ad={ads[0]} variant="sidebar" />
                  : <AdBanner variant="sidebar" />
                }

                {/* 1b. Quiz CTA */}
                <QuizCTABanner variant="sidebar" />

                {/* 2. Route Overview */}
                <Card className="fly-card p-4">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                    <Plane className="w-4 h-4 text-[#FF4500]" /> Route Overview
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Travelers logged</span>
                      <span className="font-semibold">{route?.checkin_count ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Experiences shared</span>
                      <span className="font-semibold">{posts.length}</span>
                    </div>
                    {originAirport && destAirport && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Timezone diff</span>
                        <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[130px]">
                          {originAirport.timezone?.split('/').pop()} → {destAirport.timezone?.split('/').pop()}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="fly-btn-primary w-full mt-3 text-xs h-8"
                    onClick={handleShare}
                  >
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share this Route
                  </Button>
                </Card>

                {/* 3. The Airports */}
                <Card className="fly-card p-4">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                    <MapPin className="w-4 h-4 text-[#FF4500]" /> The Airports
                  </h3>
                  <div className="space-y-2">
                    {[
                      { apt: originAirport, code: displayOrigin, label: 'From' },
                      { apt: destAirport, code: displayDest, label: 'To' },
                    ].filter(({ code }) => code && code !== 'UNK').map(({ apt, code, label }) => (
                      <Link key={code} to={`/airport/${code}`} className="group block">
                        <div className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/60 transition-colors">
                          {apt?.image_url ? (
                            <img src={apt.image_url} alt="" className="w-12 h-10 rounded-lg object-cover flex-shrink-0" loading="lazy" onError={e => e.target.style.display='none'} />
                          ) : (
                            <div className="w-12 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">{apt?.flag || '✈️'}</span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                            <p className="text-sm font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{code}</p>
                            <p className="text-xs text-muted-foreground truncate">{apt?.city || 'Airport'}, {apt?.country}</p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[#FF4500] flex-shrink-0 ml-auto transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </Card>

                {/* 4. More routes from same origin */}
                {relatedRoutes.length > 0 && (
                  <Card className="fly-card p-4">
                    <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                      <Globe className="w-4 h-4 text-[#FF4500]" />
                      More from {displayOrigin}
                    </h3>
                    <div className="space-y-1.5">
                      {relatedRoutes.map(rt => (
                        <Link key={rt.route_id} to={`/route/${rt.route_id}`} className="group flex items-center justify-between p-2 rounded-xl hover:bg-muted/60 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-mono font-semibold text-[#FF4500]">{rt.destination_code}</span>
                            <span className="text-xs text-muted-foreground truncate">
                              {rt.destination_airport?.city || rt.destination_code}
                              {rt.destination_airport?.flag ? ` ${rt.destination_airport.flag}` : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Users className="w-2.5 h-2.5" />{rt.checkin_count || 0}
                            </span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground group-hover:text-[#FF4500] transition-colors" />
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link to={`/routes?search=${displayOrigin}`}
                      className="mt-3 text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                      View all routes from {displayOrigin} →
                    </Link>
                  </Card>
                )}

                {/* 5. Explore more */}
                <Card className="fly-card p-4 bg-gradient-to-br from-[#FF4500]/5 to-[#1E3A8A]/5 border-[#FF4500]/15">
                  <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Explore More Routes</h3>
                  <p className="text-xs text-muted-foreground mb-3">Browse all 255+ active route communities</p>
                  <Link to="/routes">
                    <Button size="sm" variant="outline" className="w-full text-xs h-8 border-[#FF4500]/30 hover:border-[#FF4500] hover:text-[#FF4500]">
                      <Globe className="w-3.5 h-3.5 mr-1.5" /> Browse All Routes
                    </Button>
                  </Link>
                </Card>

              </div>
            </aside>

          </div>{/* end 2-col */}
        </div>
      </main>
      <Footer />
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
}
